import { Provider } from '@angular/core';
import { Store } from '@ngrx/store';

import { Company } from '@ninety/platform/models';
import { extractValueFromStore } from '@ninety/platform/services/state-util';
import { UsersStateActions } from '@ninety/platform/state/app-entities/users/users-state.actions';
import { selectCurrentUser } from '@ninety/platform/state/app-entities/users/users-state.selectors';
import { CompanyActions } from '@ninety/platform/state/app-global/company/company-state.actions';
import { selectCompany } from '@ninety/platform/state/app-global/company/company-state.selectors';
import { SpinnerActions } from '@ninety/platform/state/app-global/spinner/spinner-state.actions';
import {
  APPLY_UPLOADED_COMPANY_LOGO,
  ApplyUploadedCompanyLogoHandler,
  IMAGE_UPLOAD_SERVICE,
  PRIMARY_SPINNER_CONTROL,
  PrimarySpinnerControl,
  SET_AVATAR_URL,
  SetAvatarUrlHandler,
} from '@ninety/ui/core';
import { LibraryService } from '@ninety/ui/legacy/core/services/library.service';

/**
 * Binds the tokens consumed by `AvatarUploadDialogComponent` / `LogoUploadDialogComponent` (now packaged
 * in `@ninety/ui/core`) to their platform-backed implementations, inverting the dialogs' former direct
 * store dispatches and snapshot reads. This composition-root wiring is the single place that depends on
 * both the tokens and `@ninety/platform/state`. `APPLY_UPLOADED_COMPANY_LOGO` runs the exact
 * snapshot-read + conditional-dispatch orchestration the logo dialog previously ran inline. (DEV-26530
 * Part 8b)
 */
export const UPLOAD_DIALOG_PROVIDERS: Provider[] = [
  // Binds the narrow image-upload abstraction to the real LibraryService. Keeps LibraryService (and its
  // ErrorService -> Datadog vendor graph, which ng-packagr cannot compile into the public package) out
  // of `@ninety/ui/core` while the dialogs still upload through it.
  { provide: IMAGE_UPLOAD_SERVICE, useExisting: LibraryService },
  {
    provide: PRIMARY_SPINNER_CONTROL,
    useFactory: (store: Store): PrimarySpinnerControl => ({
      start: () => store.dispatch(SpinnerActions.startPrimary(null)),
      stop: () => store.dispatch(SpinnerActions.stopPrimary(null)),
    }),
    deps: [Store],
  },
  {
    provide: SET_AVATAR_URL,
    useFactory:
      (store: Store): SetAvatarUrlHandler =>
      (userId, url) =>
        store.dispatch(UsersStateActions.setAvatarUrl({ _id: userId, url })),
    deps: [Store],
  },
  {
    provide: APPLY_UPLOADED_COMPANY_LOGO,
    useFactory:
      (store: Store): ApplyUploadedCompanyLogoHandler =>
      (companyId, url) => {
        const company = extractValueFromStore(store, selectCompany);
        const currentUser = extractValueFromStore(store, selectCurrentUser);

        if (company?._id === companyId) {
          const updatedLogo = { ...company.logo, url };
          const updatedCompany = { ...company, logo: updatedLogo };
          const user = { ...currentUser, company: updatedCompany };

          /* eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially */
          store.dispatch(UsersStateActions.setCurrentUser({ userId: user._id, user }));
          store.dispatch(CompanyActions.init({ company: user.company as Company }));
        }
      },
    deps: [Store],
  },
];
